import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, InputGroup } from 'react-bootstrap';
import authService from '../services/authService';
import analyticsService from '../services/analyticsService';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate password on change
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password requirements
    const errors = validatePassword(formData.password);
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }

    setLoading(true);

    const result = await authService.register(
      formData.email,
      formData.password
    );

    setLoading(false);

    if (result.success) {
      analyticsService.auth.signup('direct');
      // Auto-login after registration
      const loginResult = await authService.login(
        formData.email,
        formData.password
      );

      if (loginResult.success) {
        navigate('/dashboard');
      } else {
        // If auto-login fails, redirect to login page
        navigate('/login');
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: 'url(/images/create-account-background.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      display: 'flex',
      alignItems: 'center'
    }}>
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
              <Card.Body className="p-4">
                <h2 className="text-center mb-4">AI Profit Engineer Registration</h2>

              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>Password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="Enter your password"
                      isInvalid={passwordErrors.length > 0 && formData.password.length > 0}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      style={{ borderColor: '#ced4da' }}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Password must be at least 8 characters and contain at least one number.
                  </Form.Text>
                  {passwordErrors.length > 0 && formData.password.length > 0 && (
                    <div className="text-danger small mt-1">
                      {passwordErrors.join('. ')}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="confirmPassword">
                  <Form.Label>Confirm Password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      placeholder="Confirm your password"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                      style={{ borderColor: '#ced4da' }}
                    >
                      {showConfirmPassword ? '🙈' : '👁️'}
                    </Button>
                  </InputGroup>
                </Form.Group>

                <Button
                  type="submit"
                  className="w-100 mb-3"
                  disabled={loading || passwordErrors.length > 0}
                  style={{
					background: 'linear-gradient(135deg, #a0731e 0%, #f7f18f 50%, #b78c30 100%)',
                    border: '2px solid #000',
                    color: '#000',
                    fontWeight: '600',
                    padding: '10px',
                    fontSize: '1.05rem',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(212, 160, 23, 0.35)'
                  }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <div className="text-center">
                  Already have an account? <Link to="/login" style={{ color: '#c0392b', textDecoration: 'none', fontWeight: '600' }}>Login</Link>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </div>
  );
}

export default Register;
